// Rota pública do link de convite (/accept-invite/:token). Valida o token via
// GET /invitations/<token> e renderiza o onboarding full-page conforme o tipo:
// workspace/organization → wizard com submit no /accept (seta cookies httpOnly);
// client (empresa) → simulação visual; user → aceite simples (entrar no workspace).
import { useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/common";
import { useAuth } from "@/auth/useAuth";
import {
  useAcceptOrganizationInvitation,
  useAcceptWorkspaceInvitation,
  useInvitation,
} from "@/hooks/useInvitations";
import { invitationService } from "@/services/invitationService";
import { setActiveWorkspaceId } from "@/lib/active-workspace";
import type { WorkspaceInvitationPrefill } from "@/types/invitation";
import { extractErrorMessage } from "@/services/axiosConfig";
import { fmtTelefone } from "@/utils/fmt";

import { ClientOnboarding } from "@/components/clients/ClientOnboarding";
import { OrgOnboarding } from "./OrgOnboarding";
import { WorkspaceOnboarding } from "./WorkspaceOnboarding";

export function PublicOnboardingRoute() {
  const token = useParams().token ?? "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const invitation = useInvitation(token);
  const acceptWs = useAcceptWorkspaceInvitation();
  const acceptOrg = useAcceptOrganizationInvitation();

  // Pós-aceite: cookies já setados → atualiza /me e vai pro app.
  const goToApp = async () => {
    await refresh();
    navigate("/", { replace: true });
  };

  if (invitation.isLoading) {
    return (
      <div className="onb-shell onb-shell-single">
        <main className="onb-content">
          <div className="onb-inner">
            <p className="onb-step-sub">Validando convite…</p>
          </div>
        </main>
      </div>
    );
  }

  if (invitation.isError || !invitation.data) {
    return (
      <div className="onb-shell onb-shell-single">
        <main className="onb-content">
          <div className="onb-inner" style={{ textAlign: "center", paddingTop: 48 }}>
            <h1 className="onb-step-title">Convite inválido ou expirado</h1>
            <p className="onb-step-sub">Solicite um novo link ao seu contato na TDAX.</p>
            <Button variant="primary" onClick={() => navigate("/login")}>
              Ir para o login
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const detail = invitation.data;

  if (detail.type === "client") {
    return (
      <ClientOnboarding
        token={token}
        orgName={detail.organization_name_suggestion ?? undefined}
        onExit={() => void goToApp()}
      />
    );
  }

  if (detail.type === "organization") {
    return (
      <OrgOnboarding
        token={token}
        workspaceName={detail.workspace_name ?? undefined}
        emailExists={detail.email_exists ?? undefined}
        emailMasked={detail.email_masked}
        onExit={() => void goToApp()}
        onSubmit={async (payload) => {
          // SEC-021: os contatos vão DENTRO do accept (rota pública). Antes eram
          // gravados depois via organizationService.setContacts (rota gated), que
          // dava 403 no owner recém-criado não-verificado e o erro era engolido.
          const result = await acceptOrg.mutateAsync({
            token,
            payload: {
              cnpj: payload.cnpj,
              legal_name: payload.legal_name,
              nickname: payload.nickname ?? null,
              accepted_tos: payload.accepted_tos,
              marketing_consent: false,
              password: payload.password ?? null,
              phone: payload.phone ?? null,
              existing_account_password: payload.existing_account_password ?? null,
              city: payload.city ?? null,
              uf: payload.uf ?? null,
              cnae: payload.cnae ?? null,
              tax_regime: payload.tax_regime ?? null,
              legal_rep_cpf: payload.legal_rep_cpf ?? null,
              contacts: payload.contacts ?? [],
            },
          });
          // SEC-052: seta o workspace ativo logo após o accept para que as chamadas
          // de follow-up (upload de certificado, /me) já levem o X-Workspace-Id.
          setActiveWorkspaceId(result.workspace_id);
          return result.organization_id ?? undefined;
        }}
        onUploadCertificate={async (_orgId, file, password) => {
          // SEC-021: usa a rota PÚBLICA atrelada ao token do convite (resolve a org
          // criada no accept no backend) em vez da rota gated
          // /{ws}/organizations/{org}/certificate — o owner recém-criado é
          // não-verificado e tomaria 403 no gate require_account_ready.
          await invitationService.uploadCertificate(token, file, password);
        }}
        submitError={acceptOrg.error ? extractErrorMessage(acceptOrg.error) : null}
        isSubmitting={acceptOrg.isPending}
      />
    );
  }

  if (detail.type === "workspace") {
    return (
      <WorkspaceOnboarding
        token={token}
        suggestedName={detail.workspace_name_suggestion ?? null}
        emailMasked={detail.email_masked}
        prefill={(detail.prefill as WorkspaceInvitationPrefill | null) ?? null}
        onExit={() => void goToApp()}
        onSubmit={async (payload) => {
          const result = await acceptWs.mutateAsync({ token, payload });
          // SEC-052: workspace ativo já após o accept (follow-ups com X-Workspace-Id).
          setActiveWorkspaceId(result.workspace_id);
        }}
        submitError={acceptWs.error ? extractErrorMessage(acceptWs.error) : null}
        isSubmitting={acceptWs.isPending}
      />
    );
  }

  // type === "user": entrar num workspace existente (aceite simples).
  return <UserInviteAccept token={token} emailExists={detail.email_exists ?? false} onDone={goToApp} />;
}

function UserInviteAccept({
  token,
  emailExists,
  onDone,
}: {
  token: string;
  emailExists: boolean;
  onDone: () => Promise<void>;
}) {
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [tos, setTos] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const needsAccount = !emailExists;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await invitationService.accept(token, {
        ...(needsAccount ? { password, phone } : {}),
        accepted_tos: tos,
        marketing_consent: false,
      });
      // SEC-052: workspace ativo já após o accept (follow-ups com X-Workspace-Id).
      setActiveWorkspaceId(result.workspace_id);
      await onDone();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="onb-shell onb-shell-single">
      <main className="onb-content">
        <form className="onb-inner" style={{ maxWidth: 460 }} onSubmit={onSubmit}>
          <div className="onb-section-head">
            <div className="onb-step-eyebrow">CONVITE</div>
            <h1 className="onb-step-title">Aceitar convite</h1>
            <p className="onb-step-sub">
              Você foi convidado para um workspace no Tributo Devido.
            </p>
          </div>
          {error && (
            <div className="onb-submit-error" role="alert">
              <span>{error}</span>
            </div>
          )}
          <div className="onb-section">
            {needsAccount && (
              <>
                <div className="onb-field">
                  <label className="onb-field-label">
                    Senha <span className="onb-field-req">*</span>
                  </label>
                  <input
                    className="onb-input"
                    type="password"
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                </div>
                <div className="onb-field">
                  <label className="onb-field-label">
                    Celular <span className="onb-field-req">*</span>
                  </label>
                  <input
                    className="onb-input"
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => setPhone(fmtTelefone(e.target.value))}
                    placeholder="(11) 90000-0000"
                  />
                </div>
              </>
            )}
            <label className="onb-checkbox" style={{ marginTop: 8 }}>
              <span
                className={`onb-checkbox-box ${tos ? "is-on" : ""}`}
                onClick={() => setTos(!tos)}
              />
              <span className="onb-checkbox-text">
                Li e aceito os Termos de uso e a Política de privacidade do TDAX.
              </span>
            </label>
          </div>
          <div className="onb-foot">
            <div className="onb-foot-left" />
            <div className="onb-foot-right">
              <Button variant="primary" type="submit" disabled={busy || !tos}>
                {busy ? "Entrando…" : "Aceitar e entrar"}
              </Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
